import { FULL_NAME, SHORT_NAME, THREE_LETTER_CODE } from '../../../packages/constants/nameTypes';
import refreshTimes from '../../../packages/constants/refreshTimes';
import { Sports } from '../../../packages/constants/sportType';
import { ISubscriberFn, OddFormat, OddsMarketTypes } from '../../../packages/types';
import envStorybookData from '../../../storybook.data.json';

export const defaultThemes = {
  dark: {},
  light: {},
  client: {
    colors: {
      hiContrast: 'white',
      sportEntityContainerPrimaryBgColor: '$gray900',
      fullTimeMatchStatusColor: '$red500',
      rowBackgroundColor: '$gray900',
      primaryBackgroundColor: '$gray900',
      oddsContainerPrimaryBgColor: '$gray900',
      pillBackgroundColor: '',
      pillActiveBackgroundColor: '',
    },
  },
};
export const subscribers: { [key: string]: ISubscriberFn[] } = {};

enum Environments {
  INTEGRATION = 'integration',
  STAGING = 'staging',
  SANDBOX = 'sandbox',
  PRODUCTION = 'production',
  DE1 = 'de1',
  DE2 = 'de2',
}

export const getEnvironment = (): Environments => {
  const urlDomain = window.location.origin;

  if (urlDomain.includes('localhost')) {
    return Environments.PRODUCTION;
  }

  const environmentValues = Object.values(Environments);
  const environmentValueForAppend = environmentValues.find((envValue: string) => urlDomain.includes(`.${envValue}.sportal365`)) ?? null;
  return environmentValueForAppend ?? Environments.PRODUCTION;
};

export const storybookEnvDataAsObject = envStorybookData[getEnvironment()];
const envApiUrls = storybookEnvDataAsObject.api;

const getGeneralVariablesBySport = (sport: Sports) => {
  const envGenerals = storybookEnvDataAsObject.generals;

  switch (sport) {
    case Sports.BASKETBALL:
      return envGenerals.basketball;
    case Sports.TENNIS:
      return envGenerals.tennis;
    case Sports.MULTISPORT:
      return envGenerals.multisport;
    case Sports.ICE_HOCKEY:
      return envGenerals.iceHockey;
    default:
      return envGenerals.football;
  }
};

const getConfigUrlBySport = (sport: Sports) => {
  switch (sport) {
    case Sports.BASKETBALL:
      return envApiUrls.basketball;
    case Sports.TENNIS:
      return envApiUrls.tennis;
    case Sports.MULTISPORT:
      return envApiUrls.multisport;
    case Sports.ICE_HOCKEY:
      return envApiUrls.multisport;
    default:
      return envApiUrls.football;
  }
};

export const generateSdkOptions = (sport: Sports) => {
  const envGeneralSportVariable = getGeneralVariablesBySport(sport);

  return {
    dataConfigApiUrl: getConfigUrlBySport(sport),
    dataConfigMultisportApiUrl: envApiUrls.multisport,
    dataConfigStandingsApiUrl: envApiUrls.standings,
    dataConfigPlayoffApiUrl: envApiUrls.playoff,
    dataConfigApiKey: envGeneralSportVariable.apiKey,
    dataConfigProject: envGeneralSportVariable.project,
    dataConfigOddClient: envGeneralSportVariable.oddClient,
    dataConfigTimezone: 'Europe/Sofia',
    // dataConfigTimezone: 'America/Detroit',
    dataConfigCompetitionList: envGeneralSportVariable.competitionList,
    dataConfigLang: 'en',
  };
};

export const footballSdkOptions = generateSdkOptions(Sports.FOOTBALL);

export const basketballSdkOptions = generateSdkOptions(Sports.BASKETBALL);

export const tennisSdkOptions = generateSdkOptions(Sports.TENNIS);

export const multisportSdkOptions = generateSdkOptions(Sports.MULTISPORT);

export const iceHockeySdkOptions = generateSdkOptions(Sports.ICE_HOCKEY);

export const languages = ['en', 'bg', 'el', 'ro', 'tr', 'de', 'pt', 'it', 'sr', 'hu', 'sk'];

export enum argCategories {
  WIDGET_ATTRIBUTES = 'widget attributes',
  THEME = 'theme',
  SDK_OPTIONS = 'sdk options',
}

export const docs = {
  dataTheme: {
    name: 'data-theme',
    description: 'Choosing a theme for the widget. Supported themes are: light , dark , client',
    control: 'radio',
    options: ['dark', 'light', 'client'],
    table: {
      defaultValue: { summary: 'light' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  themes: {
    name: 'themes',
    table: {
      category: argCategories.THEME,
    },
  },
  hiContrast: {
    control: 'color',
    description: 'Choosing a client theme text color for the widget.',
    table: {
      defaultValue: { summary: '#262626' },
      category: argCategories.THEME,
    },
  },
  primaryBackgroundColor: {
    control: 'color',
    description: 'Choosing a client theme background color for the widget.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.THEME,
    },
  },
  rowBackgroundColor: {
    control: 'color',
    description: 'Choosing a client theme row background color for the widget.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.THEME,
    },
  },
  oddsContainerPrimaryBgColor: {
    control: 'color',
    description: 'Choosing a client theme odds container color for the widget.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.THEME,
    },
  },
  pillBackgroundColor: {
    control: 'color',
    description: 'Choosing a client filter pill background color for the widget',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.THEME,
    },
  },
  pillActiveBackgroundColor: {
    control: 'color',
    description: 'Choosing a client filter pill active background color for the widget',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.THEME,
    },
  },
  dataTeamNameType: {
    name: 'data-team-name-type',
    control: 'radio',
    options: [FULL_NAME, SHORT_NAME, THREE_LETTER_CODE],
    description: 'Toggle team name type between full name, short name and three letter code.',
    table: {
      defaultValue: { summary: FULL_NAME },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataRefreshTime: {
    name: 'data-refresh-time',
    description:
      'Widget refresh time. The widget will be refreshed with the specified time interval. Options: super_fast (15 seconds), fast (30 seconds), medium (1 minute), slow (3 minutes), super_slow (10 minutes), never (no automatic updates).',
    control: 'radio',
    options: Object.keys(refreshTimes),
    table: {
      defaultValue: { summary: 'never' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataShortStatusType: {
    name: 'data-short-status-type',
    control: 'radio',
    options: [SHORT_NAME, FULL_NAME],
    description: 'Toggle status type between short name and full name.',
    table: {
      defaultValue: { summary: SHORT_NAME },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataHeaderDefaultOption: {
    name: 'data-header-default-option',
    control: 'radio',
    description: 'When data header display is false , can be passed default header option.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataHeaderDisplay: {
    name: 'data-header-display',
    description: 'Config to show or hide widget header.',
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataAdditionalInfo: {
    name: 'data-additional-info',
    description: 'Config to show or hide widget additional info.',
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  sdkBasketballOptions: {
    description: 'Config SDK options.',
    control: false,
    table: {
      defaultValue: {
        summary:
          'dataConfigApiUrl: passed by client dataConfigMultisportApiUrl: passed by client  dataConfigStandingsApiUrl: passed by client   dataConfigApiKey: passed by client  dataConfigProject: passed by client  dataConfigOddClient: passed by client   dataConfigTimezone: passed by client   dataConfigCompetitionList: passed by client   dataConfigLang: passed by client,',
      },
      category: argCategories.SDK_OPTIONS,
    },
  },
  sdkTennisOptions: {
    description: 'Config SDK options.',
    control: false,
    table: {
      defaultValue: {
        summary:
          'dataConfigApiUrl: passed by client dataConfigMultisportApiUrl: passed by client  dataConfigStandingsApiUrl: passed by client   dataConfigApiKey: passed by client  dataConfigProject: passed by client  dataConfigOddClient: passed by client   dataConfigTimezone: passed by client   dataConfigCompetitionList: passed by client   dataConfigLang: passed by client,',
      },
      category: argCategories.SDK_OPTIONS,
    },
  },
  sdkFootballOptions: {
    description: 'Config SDK options.',
    control: false,
    table: {
      // disable: true,
      defaultValue: {
        summary:
          'dataConfigApiUrl: passed by client dataConfigMultisportApiUrl: passed by client  dataConfigStandingsApiUrl: passed by client   dataConfigApiKey: passed by client  dataConfigProject: "sportal.bg"  dataConfigOddClient: passed by client  dataConfigTimezone: passed by client   dataConfigCompetitionList: passed by client   dataConfigLang: passed by client,',
      },
      category: argCategories.SDK_OPTIONS,
    },
  },
  sdkMultisportOptions: {
    description: 'Config SDK options.',
    control: false,
    table: {
      // disable: true,
      defaultValue: {
        summary:
          'dataConfigApiUrl: passed by client dataConfigMultisportApiUrl: passed by client  dataConfigApiKey: passed by client  dataConfigProject: "sportal.bg"  dataConfigTimezone: passed by client   dataConfigCompetitionList: passed by client   dataConfigLang: passed by client,',
      },
      category: argCategories.SDK_OPTIONS,
    },
  },
  sdkIceHockeyOptions: {
    description: 'Config SDK options.',
    control: false,
    table: {
      // disable: true,
      defaultValue: {
        summary:
          'dataConfigApiUrl: passed by client dataConfigMultisportApiUrl: passed by client  dataConfigApiKey: passed by client  dataConfigProject: "sportal.bg"  dataConfigTimezone: passed by client   dataConfigCompetitionList: passed by client   dataConfigLang: passed by client,',
      },
      category: argCategories.SDK_OPTIONS,
    },
  },
  dataDate: {
    name: 'data-date',
    description: 'Config to set a certain date.',
    table: {
      defaultValue: { summary: 'date:passed by client dateFormat:MM.DD.YYYY' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDateFrom: {
    name: 'data-date-from',
    description: 'Filters the response to include only items starting from a specific date.',
    table: {
      defaultValue: { summary: 'date:passed by client dateFormat:MM.DD.YYYY' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDateTo: {
    name: 'data-date-to',
    description: 'Filters the response to include only items up to a specific end date.',
    table: {
      defaultValue: { summary: 'date:passed by client dateFormat:MM.DD.YYYY' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDateFormat: {
    name: 'data-date-format',
    description: 'Sets in what format the date will be displayed. Must be in capital letters.',
    table: {
      defaultValue: { summary: 'DD.MM.YYYY' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataLabels: {
    name: 'data-labels',
    description: 'Customizes text labels for odds markets, betting options, match states, and general UI elements.',
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataStartPosition: {
    name: 'data-start-position',
    description: 'Sets the starting index for pagination when fetching data lists, allowing display from a specific position.',
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataOddsMarketValueType: {
    name: 'data-odds-market-value-type',
    description: 'Controls the format of odds value display.',
    control: 'radio',
    table: { category: argCategories.WIDGET_ATTRIBUTES },
  },
  dataOddsMarket: {
    name: 'data-odds-market',
    description: 'Specifies the type of betting market to display.',
    control: 'radio',
    table: { category: argCategories.WIDGET_ATTRIBUTES },
  },
  dataOddsBettingId: {
    name: 'data-odds-betting-id',
    description: 'Specifies the ID of the bookmaker to be displayed.',
    table: { category: argCategories.WIDGET_ATTRIBUTES },
  },
  dataOddsTabBettingId: {
    name: 'data-odds-tab-betting-id',
    description:
      'Controls which bookmakers are shown in the odds tab of the Match Center widget. If bookmakers for data-odds-betting-id are set, these same will be not shown in the Odds tab.',
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataAgeRestrictionDisplay: {
    name: 'data-age-restriction-display',
    description: 'Controls whether to show or hide age restriction next to betting providers',
    control: 'radio',
    options: ['none', 'tooltip', 'visible'],
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataOptionsToggle: {
    name: 'data-options-display',
    description: 'Control the display of the options.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataOptionsDisplay: {
    name: 'data-options-display',
    description: 'Control the display of the options with an auto option.',
    control: 'radio',
    options: ['auto', true, false],
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataOddsDisplay: {
    name: 'data-odds-display',
    description: 'Controls whether the odds should be displayed or not.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataOddsPreEventOnly: {
    name: 'data-odds-pre-event-only',
    description: 'If enabled, only shows pre-event odds and hides in-play odds.',
    table: { category: argCategories.WIDGET_ATTRIBUTES },
  },
  dataEntityLinks: {
    name: 'data-entity-links',
    description: 'Configures URL templates for navigation to teams, matches, and other entities with dynamic index replacement.',
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataPopularList: {
    name: 'data-popular-list',
    description: 'Specifies which predefined popular events list to display, based on client-specific configuration IDs.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataStage: {
    name: 'data-stage',
    description: 'Data stage id',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataCompetition: {
    name: 'data-competition',
    description: 'Data competition id',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataSeason: {
    name: 'data-season',
    description: 'Data season id',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataPlayer: {
    name: 'data-player',
    description: 'Data player id.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDisplayTabs: {
    name: 'data-display-tabs',
    description: 'Show or hide tabs.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDefaultTab: {
    name: 'data-default-tab',
    description: 'Default selected tab.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataTeam: {
    name: 'data-team',
    description: 'Data team id.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataMatchId: {
    name: 'data-match-id',
    description: 'Data match id.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataMatchType: {
    name: 'data-match-type',
    control: 'radio',
    options: ['home', 'away'],
    defaultValue: 'home',
    description: 'Change match type to be home or away',
    table: {
      defaultValue: { summary: 'home' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataSortDirection: {
    name: 'data-sort-direction',
    control: 'radio',
    options: ['asc', 'desc'],
    defaultValue: 'asc',
    description: 'Change the sort direction',
    table: {
      defaultValue: { summary: 'asc' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataSortDirectionFixtures: {
    name: 'data-sort-direction-fixtures',
    control: 'radio',
    options: ['asc', 'desc'],
    defaultValue: 'asc',
    description: 'Change the sort direction for fixtures',
    table: {
      defaultValue: { summary: 'asc' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataSortDirectionResults: {
    name: 'data-sort-direction-results',
    control: 'radio',
    options: ['asc', 'desc'],
    defaultValue: 'desc',
    description: 'Change the sort direction for results',
    table: {
      defaultValue: { summary: 'desc' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDropdownDisplay: {
    name: 'data-dropdown-display',
    description: 'Enables round dropdown menu',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataRound: {
    name: 'data-round',
    description: 'Data round ids',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataRounds: {
    name: 'data-rounds',
    description: 'Data round ids.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataRoundsFilter: {
    name: 'data-rounds-filter',
    description: 'Data round id and tournament id..',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataGroups: {
    name: 'data-groups',
    control: 'object',
    description: 'Specifies which competition groups to display in tournament standings, particularly for group-stage formats.',
    table: {
      defaultValue: {
        summary: 'passed by client',
      },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataLimit: {
    name: 'data-limit',
    description: 'Controls the maximum number of items to fetch and display in lists or tables.',
    table: {
      defaultValue: {
        summary: 'passed by client',
      },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataOffset: {
    name: 'data-offset',
    description: 'Specifies how many items to skip before starting to fetch data.',
    table: {
      defaultValue: {
        summary: 'passed by client',
      },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataStandingTypeSubcategory: {
    name: 'data-standing-type-subcategory',
    description: 'Specifies the ranking subcategory to display, such as ATP, WTA, or specific ranking types in tennis.',
    table: {
      defaultValue: {
        summary: 'passed by client',
      },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  fansUnitedEnvironment: {
    name: 'fans-united-environment',
    control: 'radio',
    options: ['dev', 'staging', 'prod'],
    description: 'Specifies which Fans United API environment to use.',
    table: {
      defaultValue: { summary: 'prod' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  fansUnitedExpanded: {
    name: 'fans-united-expanded',
    description: 'Controls whether the fan voting/prediction UI is initially displayed in expanded state.',
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  fansUnitedEnabled: {
    name: 'fans-united-enabled',
    description: 'Toggles the display of the fan voting/prediction feature for match outcomes.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  fansUnitedIdSchema: {
    name: 'fans-united-id-schema',
    description: 'Defines the ID mapping system used to identify matches in the Fans United system.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  fansUnitedToken: {
    name: 'fans-united-token',
    description: 'Authentication token for the Fans United API to identify users and enable voting capabilities.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  fansUnitedHideBeforeGameStart: {
    name: 'fans-united-hide-before-game-start',
    description: 'Number of minutes before match kickoff when the voting feature becomes available to users.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  fansUnitedShowFrom: {
    name: 'fans-united-show-from',
    description: 'Timestamp indicating when the fan voting feature should start being displayed.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataHighlightEntities: {
    name: 'data-highlight-entities',
    description: 'Array of entity IDs that should be visually emphasized in lists',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataCountryId: {
    name: 'data-country-id',
    description: 'Data country id.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataTeamIds: {
    name: 'data-team-ids',
    description: 'Data team ids.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataSportEntityOne: {
    name: 'data-sport-entity-one',
    description: 'Data for the first entity in the comparison.',
    table: { category: argCategories.WIDGET_ATTRIBUTES },
  },
  dataSportEntityTwo: {
    name: 'data-sport-entity-two',
    description: 'Data for the second entity in the comparison.',
    table: { category: argCategories.WIDGET_ATTRIBUTES },
  },
  dataElements: {
    name: 'data-elements',
    description: 'List of statistical indicators to be displayed.',
    table: { category: argCategories.WIDGET_ATTRIBUTES },
  },
  dataElementsDesktop: {
    name: 'data-elements-desktop',
    description: 'Visible data elements in the widget (on large screen)',
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
      defaultValue: {
        summary: '["matches_played", "matches_won", "match_losses", "match_draw", "goal_difference", "points", "team_form"]',
      },
    },
  },
  dataElementsMobile: {
    name: 'data-elements-mobile',
    description: 'Visible data elements in the widget (on small screen)',
    table: {
      category: argCategories.WIDGET_ATTRIBUTES,
      defaultValue: { summary: '["matches_played", "goal_difference", "points"]' },
    },
  },
  dataTournament: {
    name: 'data-tournament',
    description: 'Data tournament id.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataHomeTeamColor: {
    name: 'data-home-team-color',
    control: 'color',
    description: 'Home team color.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataAwayTeamColor: {
    name: 'data-away-team-color',
    control: 'color',
    description: 'Away team color.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataAllowInlineOdds: {
    name: 'data-allow-inline-odds',
    description: 'Config to allow inline odds display.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataImageDisplay: {
    name: 'data-image-display',
    description: 'Config to display teams or players logos or thumbs.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataAmount: {
    name: 'data-amount',
    description: 'Sets the example stake amount that is multiplied by odds to show potential returns in betting displays.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataFixed: {
    name: 'data-fixed',
    description: 'Config to display either fixed or custom mode of the widget.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataWrapperLink: {
    name: 'data-wrapper-link',
    description: "Config to display link wrapper rather than nested component's links.",
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataBettingBrandColors: {
    name: 'data-betting-brand-colors',
    description: "If enabled, odds selections will use the betting provider's brand colors.",
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataBettingLogoClickAction: {
    name: 'data-betting-logo-click-action',
    description: 'If enabled, the betting logo will switch its position with the odds selections every three seconds.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataGameType: {
    name: 'data-game-type',
    control: 'radio',
    options: ['SINGLE', 'DOUBLE', 'TEAM'],
    defaultValue: '',
    description: 'Config to display match type (singles, doubles, mixed).',
    table: {
      defaultValue: { summary: '' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataGender: {
    name: 'data-gender',
    control: 'radio',
    options: ['MALE', 'FEMALE', 'MIXED'],
    defaultValue: '',
    description: 'Config to display data by gender.',
    table: {
      defaultValue: { summary: '' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataBettingLogoOverlap: {
    name: 'data-betting-logo-overlap',
    description:
      'If enabled, the betting logo will switch its position with the odds selections. Take in mind enabling this attribute requires further development from client side. This attribute is *ignored* for the market types "CORRECT_SCORE", "FIRST_HALF_AND_FINAL_RESULT", "FIRST_PLAYER_TO_SCORE", "PLAYER_TO_SCORE_DURING_GAME", "FIRST_HALF_GOALS" and "PLAYER_TO_RECEIVE_CARD"',
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataOddsClickable: {
    name: 'data-odds-clickable',
    description: 'If enabled, the odds and betting logo are clickable with respected urls.',
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataBettingLogoDisplay: {
    name: 'data-betting-logo-display',
    description:
      'If enabled, the betting logo will be displayed below the option dropdown. Works only when data-odds-betting-id is set. Ignored when data-odds-display is activated.',
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataShowCompetitionNames: {
    name: 'data-show-competition-names',
    description: 'If enabled, the competition names will be shown in Top events section.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataNameFormat: {
    name: 'data-name-format',
    description: 'For Player/Coach names either show only the last name or the initials + last name',
    control: 'radio',
    options: ['last_name', 'initials'],
    table: {
      defaultValue: { summary: 'last_name' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataCompetitions: {
    name: 'data-competitions',
    description: 'Array of competition ids. Cannot be used with data-sport attribute.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataSport: {
    name: 'data-sport',
    description: 'Sport type. Cannot be used with data-competitions attribute.',
    table: {
      defaultValue: { summary: 'passed by client' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataMatchDisplay: {
    name: 'data-match-display',
    description: 'Controls the visibility of the match',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataStatisticsDisplay: {
    name: 'data-statistics-display',
    description: 'Controls the visibility of the player statistics',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataCurrencySymbolPosition: {
    name: 'data-currency-symbol-position',
    control: 'radio',
    options: ['prefix', 'suffix'],
    description: 'Controls currency symbol position of the odd value.',
    table: {
      defaultValue: { summary: 'prefix' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataSingleEventMainEventsDisplay: {
    name: 'data-single-event-main-events-display',
    description: 'Controls the visibility of the main events like goals and cards of the match displayed',
    table: {
      defaultValue: { summary: 'false' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataMatchCenterMainEventsDisplay: {
    name: 'data-match-center-main-events-display',
    description:
      'Controls the visibility of the main events like goals and cards of the match displayed in the Single Event widget integrated in Match Center.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDisplaySocialIcons: {
    name: 'data-display-social-icons',
    description: 'Controls the visibility of the social icons displayed in the Player and Team widget.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
  dataDisplayTeamImage: {
    name: 'data-display-team-image',
    description: 'Controls the visibility of the team image displayed in the Team widget.',
    table: {
      defaultValue: { summary: 'true' },
      category: argCategories.WIDGET_ATTRIBUTES,
    },
  },
};

export const THREE_VALUE_MARKET_TYPES = [
  OddsMarketTypes.ONETWO,
  OddsMarketTypes.ONEXTWO,
  OddsMarketTypes.OVER_UNDER,
  OddsMarketTypes.DOUBLE_CHANCE,
  OddsMarketTypes.BOTH_TO_SCORE,
  OddsMarketTypes.DRAW_NO_BET,
  OddsMarketTypes.FIRST_TEAM_TO_SCORE,
  // OddsMarketTypes.GAME_HANDICAP, // Not supported
  OddsMarketTypes.FIRST_HALF,
  OddsMarketTypes.SECOND_HALF,
  OddsMarketTypes.FULL_TIME,
];

export const MARKET_TYPES = [
  ...THREE_VALUE_MARKET_TYPES,
  OddsMarketTypes.FIRST_HALF_GOALS,
  OddsMarketTypes.CORRECT_SCORE,
  OddsMarketTypes.FIRST_PLAYER_TO_SCORE,
  OddsMarketTypes.PLAYER_TO_SCORE_DURING_GAME,
  OddsMarketTypes.PLAYER_TO_RECEIVE_CARD,
  OddsMarketTypes.FIRST_HALF_AND_FINAL_RESULT,
];

export const getApplicableMarketTypes = (sport: Sports, singleLineMarkets: boolean) => {
  if (sport === Sports.FOOTBALL) {
    return (singleLineMarkets ? THREE_VALUE_MARKET_TYPES : MARKET_TYPES).filter((market) => market !== OddsMarketTypes.ONETWO);
  }

  if (sport === Sports.BASKETBALL || sport === Sports.TENNIS) {
    return (singleLineMarkets ? THREE_VALUE_MARKET_TYPES : MARKET_TYPES).filter(
      (market) =>
        ![OddsMarketTypes.ONEXTWO, OddsMarketTypes.FIRST_HALF, OddsMarketTypes.SECOND_HALF, OddsMarketTypes.FULL_TIME].includes(market),
    );
  }

  if (sport === Sports.ICE_HOCKEY) {
    return (singleLineMarkets ? THREE_VALUE_MARKET_TYPES : MARKET_TYPES).filter(
      (market) => ![OddsMarketTypes.FIRST_HALF, OddsMarketTypes.SECOND_HALF, OddsMarketTypes.FULL_TIME].includes(market),
    );
  }
};

export const ODD_FORMATS = [OddFormat.FRACTIONAL, OddFormat.DECIMAL, OddFormat.MONEYLINE];

export const defaultEntityLinks = {
  team: {
    url: 'https://sportal.bg/sport/team-{teamId}',
  },
  competition: {
    url: 'https://sportal.bg/sport/league-{competitionId}',
  },
  standings: {
    url: 'https://sportal.bg/sport/league-{competitionId}#standings',
  },
  match: {
    url: 'https://sportal.bg/sport/match-{teamId}-{teamId}#{matchId}',
    sort: { criteria: 'alphabetically', direction: 'asc' },
  },
  slug: {
    team: 'https://sportal.bg/sport/{slug}',
    match: 'https://sportal.bg/sport/{slug}',
    competition: 'https://sportal.bg/sport/{slug}',
    standings: 'https://sportal.bg/sport/{slug}',
  },
  configuration: {
    newWindow: true,
  },
};
